/**
 * MFA-verifikation (TOTP challenge/verify) — vises af ProtectedRoute når
 * brugeren har en verificeret faktor men kun er på AAL1.
 * Kalder setMfaResolved fra useAuth ved succes.
 */
import { useState } from "react";
import { ShieldCheck, LogOut } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase/client";
import { logger } from "@/lib/logger";
import { useAuth } from "@/features/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export function MFAGate() {
  const { setMfaResolved, signOut } = useAuth();
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const verify = async (otp: string) => {
    if (otp.length !== 6 || verifying) return;
    setVerifying(true);
    try {
      const { data: factorData, error: factorError } = await supabase.auth.mfa.listFactors();
      if (factorError) throw factorError;
      const factor = factorData?.totp?.find((f) => f.status === "verified");
      if (!factor) throw new Error("Ingen verificeret MFA-faktor fundet");

      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: factor.id,
      });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factor.id,
        challengeId: challenge.id,
        code: otp,
      });
      if (verifyError) {
        toast.error("Forkert kode", { description: "Prøv igen med koden fra din authenticator-app." });
        setCode("");
        return;
      }
      setMfaResolved();
    } catch (e) {
      logger.error("mfa_verifikation_fejlede", { error: String(e) });
      toast.error("MFA-verifikation fejlede. Prøv igen.");
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <ShieldCheck className="mb-2 h-10 w-10 text-primary" aria-hidden />
          <CardTitle>To-faktor-godkendelse</CardTitle>
          <CardDescription>
            Indtast den 6-cifrede kode fra din authenticator-app.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          <InputOTP
            maxLength={6}
            value={code}
            onChange={(value) => {
              setCode(value);
              if (value.length === 6) void verify(value);
            }}
            disabled={verifying}
            aria-label="MFA-kode"
          >
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
          <Button
            className="min-h-11 w-full"
            onClick={() => void verify(code)}
            disabled={code.length !== 6 || verifying}
          >
            {verifying ? "Verificerer…" : "Bekræft"}
          </Button>
          <Button variant="ghost" className="min-h-11 w-full" onClick={() => void signOut()}>
            <LogOut className="mr-2 h-4 w-4" aria-hidden />
            Log ud
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
